import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pencil, Save, X, ImagePlus, Move, ZoomIn, GripVertical, ArrowLeftRight } from "lucide-react";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MediaPicker from "@/components/admin/MediaPicker";

export default function MainSlider() {
  const { selected } = useProvince();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const dragRef = useRef({ down: false, x: 0, dx: 0 });
  const wrapRef = useRef(null);

  // Inline edit state — only for admins. Freezes the carousel and lets the
  // user reposition/zoom/replace the cover image of the CURRENT active slide.
  const canEdit = user && ["super_admin", "province_admin", "editor"].includes(user?.role);
  const [editMode, setEditMode] = useState(false);
  const [draft, setDraft] = useState(null);        // {cover_image, cover_focus_x, cover_focus_y, cover_scale}
  const [saving, setSaving] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const coverDragRef = useRef(null);
  // Filmstrip reorder state
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(false);
  const [reorderDirty, setReorderDirty] = useState(false);

  useEffect(() => {
    // Per-province configurable limit (default 12). Admin edits it from
    // Admin → İller → slider_limit on each province row.
    const limit = Math.max(1, Number(selected?.slider_limit) || 12);
    api.get(`/news?is_in_slider=true&limit=${limit}${selected ? `&province_id=${selected.id}` : ""}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, [selected?.id, selected?.slider_limit]);

  useEffect(() => {
    if (!items.length || paused || editMode) return;
    const t = setInterval(() => setActive((a) => (a + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length, paused, editMode]);

  const goTo = (i) => setActive(((i % items.length) + items.length) % items.length);
  const prev = () => goTo(active - 1);
  const next = () => goTo(active + 1);

  // Touch + mouse swipe (disabled during edit mode)
  const onDown = (e) => {
    if (editMode) return;
    dragRef.current.down = true;
    dragRef.current.x = e.touches ? e.touches[0].clientX : e.clientX;
    dragRef.current.dx = 0;
    setPaused(true);
  };
  const onMove = (e) => {
    if (!dragRef.current.down) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    dragRef.current.dx = x - dragRef.current.x;
  };
  const onUp = () => {
    if (!dragRef.current.down) return;
    dragRef.current.down = false;
    const dx = dragRef.current.dx;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next(); else prev();
    }
    dragRef.current.dx = 0;
    setTimeout(() => setPaused(false), 500);
  };

  // ─── Inline edit helpers ─────────────────────────────────────────────
  const enterEdit = () => {
    const cur = items[active];
    if (!cur) return;
    const baseScale = typeof cur.cover_scale === "number" ? cur.cover_scale : 1;
    setDraft({
      cover_image: cur.cover_image || "",
      cover_focus_x: typeof cur.cover_focus_x === "number" ? cur.cover_focus_x : 50,
      cover_focus_y: typeof cur.cover_focus_y === "number" ? cur.cover_focus_y : 50,
      cover_scale: baseScale,
      cover_scale_x: typeof cur.cover_scale_x === "number" ? cur.cover_scale_x : baseScale,
      cover_scale_y: typeof cur.cover_scale_y === "number" ? cur.cover_scale_y : baseScale,
      cover_link_axes: false,
    });
    setEditMode(true);
    setPaused(true);
  };
  const cancelEdit = () => {
    setEditMode(false);
    setDraft(null);
    setShowPicker(false);
    setPaused(false);
  };
  const saveEdit = async () => {
    const cur = items[active];
    if (!cur || !draft) return;
    setSaving(true);
    try {
      // Strip client-only field
      const { cover_link_axes, ...payload } = draft;
      const r = await api.put(`/news/${cur.id}`, payload);
      // Merge server response into local items so the UI reflects instantly.
      setItems((prev) => prev.map((it, i) => (i === active ? { ...it, ...r.data } : it)));
      toast.success("Kapak güncellendi");
      setEditMode(false);
      setDraft(null);
      setShowPicker(false);
      setPaused(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  // ─── Reorder helpers ─────────────────────────────────────────────────
  // We reorder local `items` in-memory as the user drags, then persist the
  // whole ordering with a single call to /news/reorder-slider on demand.
  const moveItem = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return;
    setItems((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    // Keep the active slide pointing to the same news item
    setActive((cur) => {
      if (cur === from) return to;
      if (from < cur && to >= cur) return cur - 1;
      if (from > cur && to <= cur) return cur + 1;
      return cur;
    });
    setReorderDirty(true);
  };

  const onThumbDragStart = (idx) => (e) => {
    dragFromRef.current = idx;
    try { e.dataTransfer.effectAllowed = "move"; } catch {}
  };
  const onThumbDragOver = (idx) => (e) => {
    e.preventDefault();
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const onThumbDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDragOverIdx(null);
    if (from == null) return;
    moveItem(from, idx);
  };
  const onThumbDragEnd = () => {
    dragFromRef.current = null;
    setDragOverIdx(null);
  };

  const saveOrder = async () => {
    setReorderBusy(true);
    try {
      const ids = items.map((it) => it.id);
      const r = await api.post("/news/reorder-slider", { ids });
      toast.success(`Sıra kaydedildi (${r.data.updated}/${r.data.count})`);
      setReorderDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sıra kaydedilemedi");
    } finally {
      setReorderBusy(false);
    }
  };

  // Facebook-style reposition: drag on the image while in edit mode.
  const onCoverDown = (e) => {
    if (!editMode || !draft) return;
    e.preventDefault();
    e.stopPropagation();
    const box = e.currentTarget.getBoundingClientRect();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const startY = e.clientY ?? e.touches?.[0]?.clientY;
    const startFx = draft.cover_focus_x;
    const startFy = draft.cover_focus_y;
    coverDragRef.current = { box, startX, startY, startFx, startFy };
    const move = (ev) => {
      const d = coverDragRef.current;
      if (!d) return;
      const cx = ev.clientX ?? ev.touches?.[0]?.clientX;
      const cy = ev.clientY ?? ev.touches?.[0]?.clientY;
      const dx = ((cx - d.startX) / d.box.width) * 100;
      const dy = ((cy - d.startY) / d.box.height) * 100;
      setDraft((s) => ({
        ...s,
        cover_focus_x: Math.max(0, Math.min(100, d.startFx - dx)),
        cover_focus_y: Math.max(0, Math.min(100, d.startFy - dy)),
      }));
    };
    const up = () => {
      coverDragRef.current = null;
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  };

  if (!items.length) {
    return <div className="w-full aspect-[16/9] md:aspect-[16/5.5] bg-gray-100 animate-pulse rounded-lg"></div>;
  }

  const buildCoverStyle = (n, isActive) => {
    // While in edit mode use the draft state for the active slide; otherwise
    // use whatever the server has stored on the item itself. Non-uniform
    // `cover_scale_x/y` override the legacy uniform `cover_scale`.
    if (editMode && isActive && draft) {
      const sx = draft.cover_scale_x ?? draft.cover_scale ?? 1;
      const sy = draft.cover_scale_y ?? draft.cover_scale ?? 1;
      return {
        objectPosition: `${draft.cover_focus_x}% ${draft.cover_focus_y}%`,
        transform: `scale(${sx}, ${sy})`,
        transformOrigin: `${draft.cover_focus_x}% ${draft.cover_focus_y}%`,
      };
    }
    const hasCustom =
      typeof n.cover_focus_x === "number" ||
      typeof n.cover_focus_y === "number" ||
      typeof n.cover_scale_x === "number" ||
      typeof n.cover_scale_y === "number" ||
      (n.cover_scale && n.cover_scale !== 1);
    if (hasCustom) {
      const fx = n.cover_focus_x ?? 50;
      const fy = n.cover_focus_y ?? 50;
      const sx = n.cover_scale_x ?? n.cover_scale ?? 1;
      const sy = n.cover_scale_y ?? n.cover_scale ?? 1;
      return {
        objectPosition: `${fx}% ${fy}%`,
        transform: `scale(${sx}, ${sy})`,
        transformOrigin: `${fx}% ${fy}%`,
      };
    }
    return undefined;
  };

  return (
    <div
      data-testid="main-slider"
      ref={wrapRef}
      className={`main-slider-wrap relative w-full overflow-hidden rounded-lg shadow-md group bg-black ${editMode ? "cursor-move ring-2 ring-amber-400" : "cursor-grab active:cursor-grabbing"}`}
      onMouseDown={onDown}
      onMouseMove={onMove}
      onMouseUp={onUp}
      onMouseLeave={onUp}
      onTouchStart={onDown}
      onTouchMove={onMove}
      onTouchEnd={onUp}
      onMouseEnter={() => !editMode && setPaused(true)}
      onMouseOut={() => !editMode && setPaused(false)}
    >
      <div className="relative aspect-[16/9] md:aspect-[16/5.5]">
        {items.map((n, i) => {
          const isActive = i === active;
          const activeCover = editMode && isActive && draft ? draft.cover_image : n.cover_image;
          const slideInner = (
            <>
              <img
                src={activeCover}
                alt={n.title}
                className="w-full h-full object-cover select-none"
                draggable={false}
                style={buildCoverStyle(n, isActive)}
                onMouseDown={editMode && isActive ? onCoverDown : undefined}
                onTouchStart={editMode && isActive ? onCoverDown : undefined}
              />
              <div className="absolute inset-0 slider-title-overlay pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-7 text-white pointer-events-none">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {n.category && (
                    <span className="inline-block px-2 py-0.5 text-[10px] md:text-xs font-bold tracking-wide rounded-sm uppercase" style={{ background: n.category.color || "#e11d2a" }}>
                      {n.category.name}
                    </span>
                  )}
                  {n.is_breaking && <span className="inline-block px-2 py-0.5 text-[10px] md:text-xs font-bold bg-[#e11d2a] uppercase animate-pulse">SON DAKİKA</span>}
                  {n.province && <span className="text-[10px] md:text-xs text-gray-200">{n.province.name}</span>}
                </div>
                <h2 className="text-base md:text-2xl lg:text-3xl font-black leading-tight line-clamp-2 max-w-3xl drop-shadow-lg">{n.title}</h2>
                <p className="hidden md:block mt-1.5 text-xs md:text-sm text-gray-200 line-clamp-1 max-w-2xl">{n.spot}</p>
              </div>
            </>
          );
          const posClass = `main-slider-slide absolute inset-0 ${isActive ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"}`;
          const scaleStyle = { transform: isActive ? "scale(1)" : "scale(1.02)" };
          // In edit mode we render a plain <div> so clicks/drags don't navigate.
          if (editMode && isActive) {
            return (
              <div key={n.id} className={posClass} style={scaleStyle}>
                {slideInner}
              </div>
            );
          }
          return (
            <Link
              key={n.id}
              to={`/haber/${n.slug}`}
              onClick={(e) => { if (Math.abs(dragRef.current.dx) > 8) e.preventDefault(); }}
              className={posClass}
              style={scaleStyle}
            >
              {slideInner}
            </Link>
          );
        })}
      </div>

      {/* Arrows */}
      {!editMode && (
        <>
          <button data-testid="slider-prev" onClick={(e) => { e.stopPropagation(); prev(); }} className="slider-arrow absolute left-3 top-1/2 -translate-y-1/2 opacity-70 md:opacity-0 md:group-hover:opacity-100 z-20" aria-label="prev"><ChevronLeft /></button>
          <button data-testid="slider-next" onClick={(e) => { e.stopPropagation(); next(); }} className="slider-arrow absolute right-3 top-1/2 -translate-y-1/2 opacity-70 md:opacity-0 md:group-hover:opacity-100 z-20" aria-label="next"><ChevronRight /></button>
        </>
      )}

      {/* Dots */}
      {!editMode && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
          {items.map((_, i) => (
            <button key={i} onClick={(e) => { e.stopPropagation(); goTo(i); }} className={`h-1.5 rounded-full transition-all ${i === active ? "w-10 bg-[#e11d2a]" : "w-2.5 bg-white/60 hover:bg-white"}`} aria-label={`g${i}`}></button>
          ))}
        </div>
      )}

      {/* Admin toolbar */}
      {canEdit && (
        <div
          className="absolute top-3 right-3 z-30 flex items-center gap-2"
          data-testid="slider-admin-controls"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {!editMode ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); enterEdit(); }}
              data-testid="slider-edit-toggle"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white/90 hover:bg-white text-gray-800 text-xs font-semibold rounded-full shadow"
            >
              <Pencil className="w-3.5 h-3.5" /> Kapağı Düzenle
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); saveEdit(); }}
                disabled={saving}
                data-testid="slider-edit-save"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-full shadow disabled:opacity-50"
              >
                <Save className="w-3.5 h-3.5" /> {saving ? "..." : "Kaydet"}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                data-testid="slider-edit-cancel"
                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-white/90 hover:bg-white text-gray-800 text-xs font-semibold rounded-full shadow"
              >
                <X className="w-3.5 h-3.5" /> İptal
              </button>
            </>
          )}
        </div>
      )}

      {/* Filmstrip: drag-drop reorder of slider items (only in edit mode) */}
      {editMode && items.length > 1 && (
        <div
          className="absolute inset-x-0 bottom-[68px] z-30 bg-black/70 backdrop-blur px-3 py-2 flex items-center gap-2 overflow-x-auto"
          data-testid="slider-edit-filmstrip"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <span className="text-[11px] text-white/80 inline-flex items-center gap-1 shrink-0">
            <ArrowLeftRight className="w-3 h-3" /> Sırala
          </span>
          {items.map((it, idx) => (
            <div
              key={it.id}
              draggable
              onDragStart={onThumbDragStart(idx)}
              onDragOver={onThumbDragOver(idx)}
              onDrop={onThumbDrop(idx)}
              onDragEnd={onThumbDragEnd}
              onClick={() => goTo(idx)}
              data-testid={`slider-thumb-${idx}`}
              className={`group relative shrink-0 h-14 w-24 rounded overflow-hidden border-2 cursor-grab active:cursor-grabbing transition-all ${
                idx === active
                  ? "border-[#e11d2a] ring-1 ring-[#e11d2a]"
                  : dragOverIdx === idx
                    ? "border-amber-400 scale-105"
                    : "border-white/20 hover:border-white/60"
              }`}
              title={it.title}
            >
              {it.cover_image ? (
                <img src={it.cover_image} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
              ) : (
                <div className="w-full h-full bg-gray-700"></div>
              )}
              <span className="absolute top-0.5 left-0.5 text-[9px] font-bold bg-black/70 text-white rounded px-1 leading-none py-0.5">
                {idx + 1}
              </span>
              <GripVertical className="w-3 h-3 text-white/80 absolute bottom-0.5 right-0.5 opacity-60 group-hover:opacity-100" />
            </div>
          ))}
          {reorderDirty && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); saveOrder(); }}
              disabled={reorderBusy}
              data-testid="slider-edit-save-order"
              className="ml-auto shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-[#e11d2a] hover:bg-[#c01825] text-white rounded shadow disabled:opacity-50"
            >
              <Save className="w-3 h-3" /> {reorderBusy ? "..." : "Sırayı Kaydet"}
            </button>
          )}
        </div>
      )}

      {/* Edit-mode overlay: pan/zoom/replace controls at the bottom */}
      {editMode && draft && (
        <div
          className="absolute inset-x-0 bottom-0 z-30 bg-black/75 text-white p-3 flex flex-wrap items-center gap-4"
          data-testid="slider-edit-toolbar"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowPicker((s) => !s); }}
            data-testid="slider-edit-change-photo"
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded bg-white/20 hover:bg-white/30"
          >
            <ImagePlus className="w-3.5 h-3.5" /> Fotoğraf Değiştir
          </button>
          <span className="text-[11px] inline-flex items-center gap-1 opacity-90">
            <Move className="w-3.5 h-3.5" /> Sürükleyerek konumlandır
          </span>
          <label className="inline-flex items-center gap-2 text-[11px]">
            <ZoomIn className="w-3.5 h-3.5" /> X
            <input
              type="range" min="0.5" max="3" step="0.05"
              value={draft.cover_scale_x ?? 1}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setDraft({
                  ...draft,
                  cover_scale_x: v,
                  cover_scale_y: draft.cover_link_axes ? v : (draft.cover_scale_y ?? 1),
                  cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                });
              }}
              className="w-32 accent-[#e11d2a]"
              data-testid="slider-edit-scale-x"
            />
            <span className="tabular-nums w-9">{Number(draft.cover_scale_x ?? 1).toFixed(2)}x</span>
          </label>
          <label className="inline-flex items-center gap-2 text-[11px]">
            <ZoomIn className="w-3.5 h-3.5 rotate-90" /> Y
            <input
              type="range" min="0.5" max="3" step="0.05"
              value={draft.cover_scale_y ?? 1}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                setDraft({
                  ...draft,
                  cover_scale_y: v,
                  cover_scale_x: draft.cover_link_axes ? v : (draft.cover_scale_x ?? 1),
                  cover_scale: draft.cover_link_axes ? v : draft.cover_scale,
                });
              }}
              className="w-32 accent-[#e11d2a]"
              data-testid="slider-edit-scale-y"
            />
            <span className="tabular-nums w-9">{Number(draft.cover_scale_y ?? 1).toFixed(2)}x</span>
          </label>
          <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer">
            <input
              type="checkbox"
              checked={!!draft.cover_link_axes}
              onChange={(e) => setDraft({ ...draft, cover_link_axes: e.target.checked })}
              className="w-3.5 h-3.5 accent-[#e11d2a]"
              data-testid="slider-edit-link-axes"
            />
            Eksenleri Bağla
          </label>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDraft({
                ...draft,
                cover_focus_x: 50, cover_focus_y: 50,
                cover_scale: 1, cover_scale_x: 1, cover_scale_y: 1,
              });
            }}
            className="text-[11px] underline opacity-90"
          >
            Sıfırla
          </button>
          <span className="text-[10px] opacity-70 tabular-nums ml-auto">
            {draft.cover_focus_x.toFixed(0)}% / {draft.cover_focus_y.toFixed(0)}%
          </span>
        </div>
      )}

      {/* Media picker overlay */}
      {editMode && showPicker && (
        <div
          className="absolute inset-0 bg-white/95 p-4 overflow-auto z-40"
          data-testid="slider-edit-media-picker"
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div className="max-w-md mx-auto">
            <MediaPicker
              value={draft.cover_image}
              onChange={(u) => { setDraft({ ...draft, cover_image: u }); setShowPicker(false); }}
              scope="news"
              testId="slider-edit-cover-picker"
            />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowPicker(false); }}
              className="mt-3 text-xs text-gray-500 hover:underline"
            >
              Kapat
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
